import { useState, useCallback } from 'react';
import useSWR from 'swr';
import { toast } from 'sonner';
import {
  listIntegrationConnectionsApi,
  listIntegrationMessagesApi,
  getIntegrationMessageApi,
  sendIntegrationMessageApi,
  initiateIntegrationAuthApi,
  disconnectIntegrationConnectionApi,
  listIntegrationThreadsApi,
  getIntegrationThreadApi,
} from '@/api/integrations';
import type {
  NylasConnection,
  NylasMessage,
  NylasThread,
  NylasSendMessageRequestContent,
  NylasListMessagesRequestContent,
  NylasListThreadsRequestContent,
  EmailParticipant,
} from '@equaliq/api-model-v2';

/**
 * Hook to manage Nylas connections
 */
export function useNylasConnections(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  const swrKey = enabled ? ['nylas-connections'] : null;

  const { data, error, isLoading, mutate } = useSWR(
    swrKey,
    async () => {
      const response = await listIntegrationConnectionsApi();
      return response.connections ?? [];
    },
    {
      onError: (err) => {
        console.error('[useNylasConnections] Error:', err);
        toast.error('Failed to load email connections');
      },
    }
  );

  return {
    connections: data ?? [],
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to load connections') : null,
    refetch: () => mutate(),
  };
}

/**
 * Hook to initiate OAuth flow for connecting a new email account
 */
export function useConnectEmail() {
  const [isConnecting, setIsConnecting] = useState(false);

  const connectEmail = useCallback(async (provider?: string) => {
    setIsConnecting(true);
    try {
      const response = await initiateIntegrationAuthApi({ provider });

      // Open OAuth popup
      const width = 500;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        response.authUrl,
        'Connect Email',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Listen for OAuth completion
      return new Promise<boolean>((resolve) => {
        const handleMessage = (event: MessageEvent) => {
          // Validate origin to prevent malicious postMessage attacks
          const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
          const expectedOrigin = new URL(apiBaseUrl).origin;
          if (event.origin !== expectedOrigin) {
            return; // Ignore messages from unexpected origins
          }

          if (event.data?.type === 'oauth-success' && event.data?.provider === 'nylas') {
            window.removeEventListener('message', handleMessage);
            popup?.close();
            toast.success('Email account connected successfully');
            setIsConnecting(false);
            resolve(true);
          } else if (event.data?.type === 'oauth-error') {
            window.removeEventListener('message', handleMessage);
            popup?.close();
            toast.error('Failed to connect email account');
            setIsConnecting(false);
            resolve(false);
          }
        };

        window.addEventListener('message', handleMessage);

        // Handle popup closed manually
        const checkClosed = setInterval(() => {
          if (popup?.closed) {
            clearInterval(checkClosed);
            window.removeEventListener('message', handleMessage);
            setIsConnecting(false);
            resolve(false);
          }
        }, 500);
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initiate connection';
      toast.error(errorMessage);
      setIsConnecting(false);
      return false;
    }
  }, []);

  return { connectEmail, isConnecting };
}

/**
 * Hook to disconnect an email account
 */
export function useDisconnectEmail() {
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const disconnectEmail = useCallback(async (connectionId: string) => {
    setIsDisconnecting(true);
    try {
      await disconnectIntegrationConnectionApi({ connectionId });
      toast.success('Email account disconnected');
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to disconnect';
      toast.error(errorMessage);
      return false;
    } finally {
      setIsDisconnecting(false);
    }
  }, []);

  return { disconnectEmail, isDisconnecting };
}

/**
 * Hook to manage messages for a specific connection
 */
export function useNylasMessages(connectionId: string | null) {
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [extraPages, setExtraPages] = useState<NylasMessage[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const swrKey = connectionId ? ['nylas-messages', connectionId] : null;

  const { data, error, isLoading, mutate } = useSWR(
    swrKey,
    async () => {
      const response = await listIntegrationMessagesApi({ connectionId: connectionId!, limit: 50 });
      setNextCursor(response.nextCursor);
      setExtraPages([]);
      return response.data ?? [];
    },
    {
      onError: () => toast.error('Failed to load messages'),
    }
  );

  const loadMore = useCallback(async () => {
    if (!connectionId || !nextCursor || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const response = await listIntegrationMessagesApi({ connectionId, limit: 50, cursor: nextCursor });
      setExtraPages(prev => [...prev, ...(response.data ?? [])]);
      setNextCursor(response.nextCursor);
    } catch {
      toast.error('Failed to load more messages');
    } finally {
      setIsLoadingMore(false);
    }
  }, [connectionId, nextCursor, isLoadingMore]);

  const filterMessages = useCallback(
    (filters: Partial<NylasListMessagesRequestContent>) => {
      mutate(async () => {
        const response = await listIntegrationMessagesApi({ connectionId: connectionId!, limit: 50, ...filters });
        setNextCursor(response.nextCursor);
        setExtraPages([]);
        return response.data ?? [];
      });
    },
    [connectionId, mutate]
  );

  const messages = [...(data ?? []), ...extraPages];

  return {
    messages,
    isLoading: isLoading || isLoadingMore,
    error: error ? (error instanceof Error ? error.message : 'Failed to load messages') : null,
    nextCursor,
    refetch: () => mutate(),
    loadMore,
    filterMessages,
  };
}

/**
 * Hook to get a specific message by ID
 */
export function useNylasMessage(connectionId: string | null, messageId: string | null) {
  const swrKey = connectionId && messageId ? ['nylas-message', connectionId, messageId] : null;

  const { data, error, isLoading } = useSWR(
    swrKey,
    async () => {
      const response = await getIntegrationMessageApi({ connectionId: connectionId!, messageId: messageId! });
      return response.data;
    },
    {
      onError: () => toast.error('Failed to load message details'),
    }
  );

  return {
    message: data ?? null,
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to load message') : null,
  };
}

/**
 * Hook to send messages
 */
export function useSendMessage() {
  const [isSending, setIsSending] = useState(false);

  const sendMessage = useCallback(async (
    connectionId: string,
    to: EmailParticipant[],
    subject: string,
    body: string,
    options?: {
      cc?: EmailParticipant[];
      bcc?: EmailParticipant[];
      replyToMessageId?: string;
    }
  ) => {
    setIsSending(true);
    try {
      const request: NylasSendMessageRequestContent = {
        connectionId,
        to,
        subject,
        body,
        ...options,
      };
      const response = await sendIntegrationMessageApi(request);
      toast.success('Message sent successfully');
      return response.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      toast.error(errorMessage);
      throw err;
    } finally {
      setIsSending(false);
    }
  }, []);

  return { sendMessage, isSending };
}

/**
 * Hook to manage threads for a specific connection
 * Threads include AI-generated metadata (summary, priority, labels, keywords)
 */
export function useNylasThreads(connectionId: string | null) {
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [extraPages, setExtraPages] = useState<NylasThread[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const swrKey = connectionId ? ['nylas-threads', connectionId] : null;

  const { data, error, isLoading, mutate } = useSWR(
    swrKey,
    async () => {
      const response = await listIntegrationThreadsApi({ connectionId: connectionId!, limit: 50 });
      setNextToken(response.nextToken);
      setExtraPages([]);
      return response.threads ?? [];
    },
    {
      onError: (err) => {
        console.error('[useNylasThreads] Error:', err);
        toast.error('Failed to load threads');
      },
    }
  );

  const loadMore = useCallback(async () => {
    if (!connectionId || !nextToken || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const response = await listIntegrationThreadsApi({ connectionId, limit: 50, nextToken });
      setExtraPages(prev => [...prev, ...(response.threads ?? [])]);
      setNextToken(response.nextToken);
    } catch {
      toast.error('Failed to load more threads');
    } finally {
      setIsLoadingMore(false);
    }
  }, [connectionId, nextToken, isLoadingMore]);

  const filterThreads = useCallback(
    (filters: Partial<NylasListThreadsRequestContent>) => {
      mutate(async () => {
        const response = await listIntegrationThreadsApi({ connectionId: connectionId!, limit: 50, ...filters });
        setNextToken(response.nextToken);
        setExtraPages([]);
        return response.threads ?? [];
      });
    },
    [connectionId, mutate]
  );

  const threads = [...(data ?? []), ...extraPages];

  return {
    threads,
    isLoading: isLoading || isLoadingMore,
    error: error ? (error instanceof Error ? error.message : 'Failed to load threads') : null,
    nextToken,
    refetch: () => mutate(),
    loadMore,
    filterThreads,
  };
}

/**
 * Hook to get a specific thread by ID
 */
export function useNylasThread(threadMetadataId: string | null) {
  const swrKey = threadMetadataId ? ['nylas-thread', threadMetadataId] : null;

  const { data, error, isLoading } = useSWR(
    swrKey,
    async () => {
      const response = await getIntegrationThreadApi({ threadMetadataId: threadMetadataId! });
      return response.thread;
    },
    {
      onError: () => toast.error('Failed to load thread details'),
    }
  );

  return {
    thread: data ?? null,
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to load thread') : null,
  };
}